// @ts-nocheck
import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import ProductImageBadges from "@insite/content-library/Components/ProductImageBadges";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import AlertCircleLine, {
    AlertCircleLineOffset,
} from "@store/Library/Components/Toolkit/Icons/AlertCircle/AlertCircleLine";
import PinLine, { PinLineOffset } from "@store/Library/Components/Toolkit/Icons/Pin/PinLine";
import Typography, { TypographyPresentationProps } from "@store/MobiusOverrides/Typography/Typography";
import ProductDescription from "@store/Overrides/Components/ProductDescription";
import {
    calculateProductAdditionalCustomerProductNames,
    getMultiLocationCatalogResult,
} from "@store/Framework/Common/Utilities/multiLocationCatalogUtilities";
import Tooltip, { TooltipPresentationProps } from "@store/MobiusOverrides/Tooltip/Tooltip";
import ProductPartNumbers from "@store/Overrides/Components/ProductPartNumbers";
import {
    productInformationStyles,
    ProductListProductInformationProps,
    ProductListProductInformationStyles,
} from "@store/Overrides/Widgets/ProductList/ProductListProductInformation";
import { linkActionStyles } from "@store/Themes/common/mixins/link";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { iconScalableStyles } from "@toolkit/Themes/common/mixins/icon";
import { spacingStyles } from "@toolkit/Themes/common/mixins/spacing";
import React, { FC, useContext } from "react";
import { css } from "styled-components";

const otherShipTosDisplayCount = 4;

export interface ProductListMultiLocationCatalogProductInformationProps
    extends Pick<
        ProductListProductInformationProps,
        "showTitle" | "showPartNumbers" | "showImageBadges" | "maxImageBadges" | "showTextBadges" | "maxTextBadges"
    > {}

export interface ProductListMultiLocationCatalogProductInformationStyles
    extends Pick<
        ProductListProductInformationStyles,
        "attributesWrapper" | "descriptionStyles" | "partNumbersStyles" | "productImageBadges" | "productTextBadges"
    > {
    shipTosWrapper?: InjectableCss;
    shipTosTitle?: TypographyPresentationProps;
    shipTosTitleIcon?: IconPresentationProps;
    shipTosList?: InjectableCss;
    shipTosListItem?: InjectableCss;
    shipToName?: TypographyPresentationProps;
    shipTosMoreIcon?: IconPresentationProps;
    shipTosMoreTooltip?: TooltipPresentationProps;
}

export const multiLocationCatalogProductInformationStyles: ProductListMultiLocationCatalogProductInformationStyles = {
    attributesWrapper: productInformationStyles.attributesWrapper,
    partNumbersStyles: productInformationStyles.partNumbersStyles,
    productTextBadges: productInformationStyles.productTextBadges,
    productImageBadges: productInformationStyles.productImageBadges,
    descriptionStyles: productInformationStyles.descriptionStyles,
    shipTosWrapper: {
        css: css`
            font-size: ${x => x.theme.design.typography.size.smaller};
            line-height: ${x => x.theme.design.typography.lineHeight.semi};
        `,
    },
    shipTosTitle: {
        css: css`
            ${spacingStyles(null, pxToRem(7))}
            display: flex;
            align-items: flex-start;
            gap: ${pxToRem(5)};
        `,
    },
    shipTosTitleIcon: {
        src: PinLine,
        css: css`
            ${iconScalableStyles(pxToRem(18), PinLineOffset)}
            flex-shrink: 0;
            margin-top: ${pxToRem(2)};
        `,
    },
    shipTosList: {
        css: css`
            ${spacingStyles(null, pxToRem(6))}
        `,
    },
    shipTosListItem: {
        css: css`
            ${spacingStyles(null, pxToRem(6))}
            display: flex;
            align-items: flex-start;
            gap: ${pxToRem(4)};

            &::before {
                content: "";
                flex-shrink: 0;
                width: ${pxToRem(4)};
                height: ${pxToRem(4)};
                border-radius: 50%;
                background-color: currentColor;
                margin-top: ${pxToRem(8)};
            }
        `,
    },
    shipToName: { ellipsis: true },
    shipTosMoreTooltip: {
        mergeCss: true,
        typographyProps: {
            size: "inherit",
            lineHeight: "inherit",
        },
        cssOverrides: {
            tooltipWrapper: css`
                width: 100%;
                margin-left: 0;
            `,
            tooltipClickable: css`
                ${linkActionStyles()}
                display: flex;
                align-items: flex-start;
                gap: ${pxToRem(5)};
            `,
            tooltipContainer: css`
                left: -${pxToRem(31)};
                transform: none;
                width: calc(100% + ${pxToRem(62)});
                justify-content: flex-start;
                margin-bottom: ${pxToRem(13)};
            `,
            tooltipArrow: css`
                top: calc(100% - ${pxToRem(3)});
                left: ${pxToRem(38)};
                border-width: ${pxToRem(16)};
                border-top-color: ${getColor("common.background")};
            `,
            tooltipBody: css`
                overflow-y: auto;
                width: 100%;
                max-height: ${pxToRem(300)};
                text-align: left;
                padding: ${pxToRem(16)};
                background-color: ${getColor("common.background")};
                box-shadow: 0 4px 24px 0 rgba(128, 128, 128, 0.25);
            `,
        },
    },
    shipTosMoreIcon: {
        src: AlertCircleLine,
        color: "inherit",
        css: css`
            ${iconScalableStyles(pxToRem(18), AlertCircleLineOffset)}
            flex-shrink: 0;
            margin-top: ${pxToRem(2)};
            transition: inherit;
        `,
    },
};

const styles = multiLocationCatalogProductInformationStyles;

const ProductListMultiLocationCatalogProductInformation: FC<ProductListMultiLocationCatalogProductInformationProps> = ({
    showTitle,
    showPartNumbers,
    showImageBadges,
    maxImageBadges,
    showTextBadges,
    maxTextBadges,
}) => {
    const productContext = useContext(ProductContext);
    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });
    const { product } = productContext;

    if (!product) {
        return null;
    }

    const multiLocationCatalogResult = getMultiLocationCatalogResult(product);

    if (!multiLocationCatalogResult) {
        return null;
    }

    const customerProductNames = calculateProductAdditionalCustomerProductNames(
        product.customerProductNumber,
        multiLocationCatalogResult,
    );

    const otherShipTos = Object.entries(multiLocationCatalogResult.availableAtOtherShipTos);
    const otherShipTosCount = otherShipTos.length;
    const moreShipTosCount =
        otherShipTosCount > otherShipTosDisplayCount ? otherShipTosCount - otherShipTosDisplayCount : 0;

    const renderOtherShipTos = (shipTos = otherShipTos) => (
        <>
            <Typography {...styles.shipTosTitle}>
                <Icon {...styles.shipTosTitleIcon} />
                {translate("{0}:", translate("Previously Purchased by"))}
            </Typography>
            <StyledWrapper as="ul" {...styles.shipTosList}>
                {shipTos.map(([shipToId, shipTo]) => (
                    <StyledWrapper as="li" {...styles.shipTosListItem} key={shipToId}>
                        <Typography {...styles.shipToName}>{shipTo.customerName}</Typography>
                    </StyledWrapper>
                ))}
            </StyledWrapper>
        </>
    );

    return (
        <>
            {showPartNumbers && (
                <StyledWrapper {...styles.attributesWrapper}>
                    <ProductPartNumbers
                        productNumber={product.productNumber}
                        customerProductNumber={customerProductNames.primary}
                        manufacturerItem={product.manufacturerItem}
                        extendedStyles={styles.partNumbersStyles}
                        additionalCustomerProductNumberCount={customerProductNames.additionalCount}
                    />
                </StyledWrapper>
            )}
            {showTitle && <ProductDescription product={productContext} extendedStyles={styles.descriptionStyles} />}
            {showTextBadges && textBadges && (
                <ProductTextBadges
                    badgeList={textBadges}
                    maxBadges={maxTextBadges ?? 4}
                    extendedStyles={styles.productTextBadges}
                />
            )}
            {showImageBadges && imageBadges && (
                <ProductImageBadges
                    badgeList={imageBadges}
                    maxBadges={maxImageBadges ?? 4}
                    extendedStyles={styles.productImageBadges}
                />
            )}
            {Boolean(otherShipTosCount) && (
                <StyledWrapper {...styles.shipTosWrapper}>
                    {renderOtherShipTos(otherShipTos.slice(0, otherShipTosDisplayCount))}
                    {Boolean(moreShipTosCount) && (
                        <Tooltip
                            {...styles.shipTosMoreTooltip}
                            triggerAltText={null}
                            triggerComponent={
                                <>
                                    <Icon {...styles.shipTosMoreIcon} />
                                    {translate("{0} more", `${moreShipTosCount}`)}
                                </>
                            }
                            text={renderOtherShipTos()}
                        />
                    )}
                </StyledWrapper>
            )}
        </>
    );
};

export default ProductListMultiLocationCatalogProductInformation;
